import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { normalize } from '../../utils/normalize';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';
import { groupLogicalInventory } from '../../utils/logicalInventory';

const VEHICLES = [
  'Camion Grande',
  'Camion Mediano Azul',
  'Camion Mediano Rojo',
  'Camion Chico',
  'Fiorino Andres',
  'Fiorino Alallon',
  'Vehículo particular',
];

const STATUS_LABELS = {
  draft: { label: 'Borrador', color: 'var(--text-dim)' },
  in_transit: { label: 'En tránsito', bg: 'var(--yellow-dim, #3d2e00)', color: 'var(--yellow, #f59e0b)' },
  completed: { label: 'Completado', bg: 'var(--green-dim)', color: 'var(--green)' },
};

function fmtDt(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false, hourCycle: 'h23' });
}

const EMPTY_SENT = { inventory_item_ids: new Set(), pack_ids: new Set(), checklist_item_ids: new Set(), details: {} };

function isSentFn(item, links, sentData) {
  if (sentData.checklist_item_ids.has(item.id)) return true;
  const itemLinks = links[item.id] || [];
  return itemLinks.some(l =>
    (l.inventory_item_id && sentData.inventory_item_ids.has(l.inventory_item_id)) ||
    (l.pack_id && sentData.pack_ids.has(l.pack_id))
  );
}

function getSentInfoFn(item, links, sentData) {
  if (sentData.checklist_item_ids.has(item.id)) return sentData.details[`ci_${item.id}`];
  const itemLinks = links[item.id] || [];
  for (const l of itemLinks) {
    if (l.inventory_item_id && sentData.inventory_item_ids.has(l.inventory_item_id)) {
      return sentData.details[l.inventory_item_id];
    }
    if (l.pack_id && sentData.pack_ids.has(l.pack_id)) {
      return sentData.details[l.pack_id];
    }
  }
  return null;
}

export default function LogisticsModal({ checklist, transfers: initialTransfers, links, items, onClose, onTransferCreated }) {
  const { user } = useAuth();
  const isAdmin = canWrite(user, 'logistica');
  const [localTransfers, setLocalTransfers] = useState(initialTransfers);
  const [vehicle, setVehicle] = useState('');
  // Por defecto, el destino es el lugar del evento (levantado de la agenda).
  const [destinoName, setDestinoName] = useState(checklist.event_place || '');
  const [destinoId, setDestinoId] = useState(null);
  const [destinoSuggestions, setDestinoSuggestions] = useState([]);
  const [eventPlaceSuggestions, setEventPlaceSuggestions] = useState([]);
  const [showDestinoSuggestions, setShowDestinoSuggestions] = useState(false);
  const [destinoSimilar, setDestinoSimilar] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [sentData, setSentData] = useState(EMPTY_SENT);
  const [loadingSent, setLoadingSent] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const consolidatedItems = items.filter(i => (links[i.id] || []).length > 0);
  const unconsolidatedItems = items.filter(i => (links[i.id] || []).length === 0);
  const allItems = [...consolidatedItems, ...unconsolidatedItems];

  // Initialize selection based on checklist is_checked state
  useEffect(() => {
    const initial = {};
    for (const item of allItems) initial[item.id] = !!item.is_checked;
    setSelectedItems(initial);
  }, []); // eslint-disable-line


  // Load suggested locations based on event_place
  useEffect(() => {
    if (!checklist.event_place) return;
    const words = checklist.event_place.trim().split(/\s+/).slice(0, 2).join(' ');
    if (!words) return;
    api.get(`/locations?search=${encodeURIComponent(words)}`)
      .then(r => setEventPlaceSuggestions(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line

  // Load sent items via single endpoint, then deselect them
  useEffect(() => {
    let cancelled = false;
    api.get(`/transfers/sent-items?checklist_id=${checklist.id}`)
      .then(res => {
        if (cancelled) return;
        const d = res.data;
        const sd = {
          inventory_item_ids: new Set(d.inventory_item_ids),
          pack_ids: new Set(d.pack_ids),
          checklist_item_ids: new Set(d.checklist_item_ids),
          details: d.details,
        };
        setSentData(sd);
        setSelectedItems(prev => {
          const next = { ...prev };
          for (const item of allItems) {
            if (isSentFn(item, links, sd)) next[item.id] = false;
          }
          return next;
        });
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoadingSent(false); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  // Debounced destino suggestions
  useEffect(() => {
    if (destinoName.length < 2) { setDestinoSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/locations?search=${encodeURIComponent(destinoName)}`);
        setDestinoSuggestions(data);
      } catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(t);
  }, [destinoName]);

  const toggleItem = (id) => setSelectedItems(prev => ({ ...prev, [id]: !prev[id] }));

  const enabledItems = allItems.filter(i => !isSentFn(i, links, sentData));
  const allEnabled = enabledItems.length > 0 && enabledItems.every(i => selectedItems[i.id]);
  const selectedCount = enabledItems.filter(i => selectedItems[i.id]).length;

  const handleSelectAll = () => {
    setSelectedItems(prev => {
      const next = { ...prev };
      for (const i of enabledItems) next[i.id] = !allEnabled;
      return next;
    });
  };

  const dropdownItems = destinoName === '' ? eventPlaceSuggestions : destinoSuggestions;
  const showDropdown = showDestinoSuggestions && dropdownItems.length > 0;

  const handleCreate = async (forceNewDestino = false) => {
    setError('');
    setDestinoSimilar([]);
    if (selectedCount === 0) { setError('Seleccioná al menos un ítem'); return; }
    if (!destinoName.trim()) { setError('Ingresá el destino'); return; }
    setSaving(true);
    try {
      // 1. Resolve to_location
      let toLocationId = destinoId;
      if (!toLocationId) {
        const allRes = await api.get('/locations');
        const existing = allRes.data.find(l => normalize(l.name) === normalize(destinoName.trim()));
        if (existing) {
          toLocationId = existing.id;
        } else if (!forceNewDestino) {
          const words = destinoName.trim().split(/\s+/).slice(0, 3).join(' ');
          const searchRes = await api.get(`/locations?search=${encodeURIComponent(words)}`);
          const similar = searchRes.data.filter(l => normalize(l.name) !== normalize(destinoName.trim()));
          if (similar.length > 0) {
            setDestinoSimilar(similar);
            setSaving(false);
            return;
          }
          const created = await api.post('/locations', { name: destinoName.trim(), force: true });
          toLocationId = created.data.id;
        } else {
          const created = await api.post('/locations', { name: destinoName.trim(), force: true });
          toLocationId = created.data.id;
        }
      }

      // 2. Get home location
      const locationsRes = await api.get('/locations');
      const homeLocation = locationsRes.data.find(l => l.is_home);
      if (!homeLocation) {
        setError('No se encontró la locación base (Depósito)');
        setSaving(false);
        return;
      }

      // 3. Create transfer
      const transferRes = await api.post('/transfers', {
        from_location_id: homeLocation.id,
        to_location_id: toLocationId,
        vehicle: vehicle || null,
        checklist_id: checklist.id,
        event_id: checklist.event_id,
      });
      const transferId = transferRes.data.id;

      // 4. Add items (only enabled selected items)
      const itemsToAdd = allItems.filter(i => selectedItems[i.id] && !isSentFn(i, links, sentData));
      for (const item of itemsToAdd) {
        const itemLinks = links[item.id] || [];
        if (itemLinks.length === 0) {
          await api.post(`/transfers/${transferId}/items`, { checklist_item_name: item.current_name, checklist_item_id: item.id });
        } else {
          for (const link of itemLinks) {
            if (link.pack_id) {
              await api.post(`/transfers/${transferId}/items`, { pack_id: link.pack_id });
            } else if (link.inventory_item_id) {
              await api.post(`/transfers/${transferId}/items`, { inventory_item_id: link.inventory_item_id });
            }
          }
        }
      }

      // 5. Dispatch
      await api.patch(`/transfers/${transferId}/dispatch`);

      onTransferCreated();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear la logística');
      setSaving(false);
    }
  };

  const renderItem = (item, isConsolidated) => {
    const itemLinks = links[item.id] || [];
    const logicalLinks = groupLogicalInventory(itemLinks);
    const checked = !!selectedItems[item.id];
    const sent = isSentFn(item, links, sentData);
    const sentInfo = sent ? getSentInfoFn(item, links, sentData) : null;

    return (
      <div
        key={item.id}
        onClick={sent ? undefined : () => toggleItem(item.id)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '6px 8px', borderRadius: 6,
          cursor: sent ? 'default' : 'pointer',
          background: checked && !sent ? 'var(--surface2)' : undefined,
          opacity: sent ? 0.55 : 1,
        }}
      >
        <div style={{
          width: 16, height: 16, borderRadius: 3, flexShrink: 0, marginTop: 2,
          border: '1.5px solid var(--border)',
          background: checked && !sent ? 'var(--accent, #6366f1)' : undefined,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {checked && !sent && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isConsolidated && !sent ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 500 }}>
                {logicalLinks.map(group => `${group.items.length} × ${group.name}`).join(', ')}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{item.current_name}</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13 }}>{item.current_name}</div>
              {!isConsolidated && !sent && (
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>Sin consolidar</div>
              )}
              {sent && sentInfo && (
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                  Enviado en {sentInfo.vehicle || '—'} · {fmtDt(sentInfo.created_at)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const handleDeleteTransfer = async () => {
    try {
      await api.delete(`/transfers/${deleteConfirm.id}`);
      setLocalTransfers(prev => prev.filter(x => x.id !== deleteConfirm.id));
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    } finally {
      setDeleteConfirm(null);
    }
  };

  return (
    <>
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 520, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-title" style={{ marginBottom: 16 }}>Logística de carga</div>

        {/* ── Transfers existentes ── */}
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Logísticas anteriores</div>
        {localTransfers.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 12 }}>No hay logísticas iniciadas.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {localTransfers.map(t => {
              const s = STATUS_LABELS[t.status] || {};
              return (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', background: 'var(--surface2)', borderRadius: 6, fontSize: 13,
                }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 500 }}>{t.vehicle || '—'}</span>
                    <span style={{ color: 'var(--text-dim)', fontSize: 11, marginLeft: 6 }}>
                      {t.from_location_name} → {t.to_location_name}
                    </span>
                    <span style={{ color: 'var(--text-dim)', fontSize: 11, marginLeft: 6 }}>
                      · {t.item_count} ítems
                    </span>
                  </span>
                  <span style={{
                    fontSize: 11, padding: '2px 7px', borderRadius: 4, flexShrink: 0,
                    background: s.bg || 'var(--surface)', color: s.color,
                    fontWeight: 600,
                  }}>
                    {s.label}
                  </span>
                  {(t.status !== 'completed' || isAdmin) && (
                    <button
                      className="btn btn-icon btn-ghost"
                      style={{ color: 'var(--text-dim)', width: 24, height: 24, flexShrink: 0 }}
                      title="Eliminar logística"
                      onClick={async () => {
                        setDeleteConfirm(t);
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6M14 11v6"/>
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ height: 1, background: 'var(--border)', margin: '4px 0 14px' }} />

        {/* ── Nueva logística ── */}
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Nueva logística</div>

        {error && <div className="inv-error" style={{ marginBottom: 8 }}>{error}</div>}

        <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Vehículo */}
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>Vehículo</label>
            <select className="input" value={vehicle} onChange={e => setVehicle(e.target.value)}>
              <option value="">SIN ESPECIFICAR</option>
              {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          {/* Destino */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>Destino</label>
            <input
              className="input"
              placeholder="Escribí el destino..."
              value={destinoName}
              onChange={e => { setDestinoName(e.target.value); setDestinoId(null); setShowDestinoSuggestions(true); setDestinoSimilar([]); }}
              onFocus={() => setShowDestinoSuggestions(true)}
              onBlur={() => setTimeout(() => setShowDestinoSuggestions(false), 150)}
              onKeyDown={e => {
                if (e.key === 'Enter' && showDropdown && dropdownItems.length > 0) {
                  e.preventDefault();
                  const first = dropdownItems[0];
                  setDestinoName(first.name);
                  setDestinoId(first.id);
                  setShowDestinoSuggestions(false);
                }
              }}
            />
            {showDropdown && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)', marginTop: 2,
              }}>
                {dropdownItems.map(loc => (
                  <div
                    key={loc.id}
                    onMouseDown={() => { setDestinoName(loc.name); setDestinoId(loc.id); setShowDestinoSuggestions(false); }}
                    style={{ padding: '10px 12px', minHeight: 44, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    {loc.name}
                    {loc.is_home && <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>depósito</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Similar locations warning */}
          {destinoSimilar.length > 0 && (
            <div style={{
              padding: '10px 12px', borderRadius: 6, marginTop: -4,
              background: 'var(--yellow-dim, #3d2e00)', border: '1px solid var(--yellow, #f59e0b)',
            }}>
              <div style={{ fontSize: 12, color: 'var(--yellow, #f59e0b)', fontWeight: 600, marginBottom: 6 }}>
                ¿Quisiste decir alguna de estas?
              </div>
              {destinoSimilar.map(loc => (
                <div
                  key={loc.id}
                  onMouseDown={() => { setDestinoName(loc.name); setDestinoId(loc.id); setDestinoSimilar([]); }}
                  style={{ fontSize: 13, padding: '3px 0', cursor: 'pointer', color: 'var(--text)' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  · {loc.name}
                  {loc.is_home && <span style={{ fontSize: 10, color: 'var(--text-dim)', marginLeft: 5 }}>depósito</span>}
                </div>
              ))}
              <button
                className="btn btn-ghost"
                style={{ marginTop: 8, fontSize: 12, padding: '3px 8px' }}
                onClick={() => handleCreate(true)}
                disabled={saving}
              >
                No, crear "{destinoName.trim()}"
              </button>
            </div>
          )}

          {/* Ítems */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <label style={{ fontSize: 12, color: 'var(--text-dim)' }}>Ítems a transportar</label>
              {enabledItems.length > 0 && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ fontSize: 11, padding: '2px 8px', height: 'auto' }}
                  onClick={handleSelectAll}
                >
                  {allEnabled ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </button>
              )}
            </div>
            {loadingSent ? (
              <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '8px 0' }}>Verificando ítems enviados...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {consolidatedItems.map(item => renderItem(item, true))}
                {unconsolidatedItems.map(item => renderItem(item, false))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 12, marginTop: 10, borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button
            className="btn btn-primary"
            onClick={handleCreate}
            disabled={saving || selectedCount === 0}
          >
            {saving ? 'Creando...' : `Crear logística (${selectedCount} ítems)`}
          </button>
        </div>
      </div>
    </div>
    {deleteConfirm && (
      <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <h3 className="modal-title">Eliminar logística</h3>
          <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 8 }}>
            Vas a eliminar la logística:
          </p>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>
            {deleteConfirm.vehicle || 'Sin vehículo'}
          </p>
          <p style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 24 }}>
            {deleteConfirm.status !== 'completed' ? 'Si está en tránsito, los ítems vuelven al depósito.' : 'Esta acción no se puede deshacer.'}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancelar</button>
            <button className="btn btn-danger" onClick={handleDeleteTransfer}>Eliminar</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
